import L, { FeatureGroup } from "leaflet";
import "leaflet/dist/leaflet.css";
import mustache from "mustache";
import { GOOGLE_MAPS_API_KEY } from "../../../../constantes";
import { BaseResponse } from "../../../base/response/BaseResponse";
import { serviceProvider } from "../../../comun/provider/ServiceProvider";
import { ClienteFincaBaseResponse } from "../../../modulos/terceros/response/ClienteFincaBaseResponse";
import { ClienteFincaLoteResponse } from "../../../modulos/terceros/response/ClienteFincaLoteResponse";
import { fx_createLoteGrpcService } from "../../../modulos/terceros/services/LoteGrpcService";
import { _2d } from "../../../utils/numberUtils";
import { IMapRender } from "../../definitions/map/IMapRender";
import { fx_acotarMapaA } from "../../implementations/map/mapLimites";
import { RigthActions } from "../../implementations/map/RigthActions";
import {
  ConfigTopActions,
  TopActions,
} from "../../implementations/map/TopActions";
import popupTemplate from "./popupTemplate.html";
import template from "./template.html";
import { Tooltip } from "../../implementations/map/TooltipRender";
import { LoteEditModal } from "../../modulos/terceros/lote/modal/LoteEditModal";

mustache.parse(template);
mustache.parse(popupTemplate);

/** Render del mapa base. Orquesta la creación del mapa Leaflet, el dibujado de
 *  lotes como capas GeoJSON, los controles laterales/superiores, y la selección
 *  de lote con popup y estilo. Las subclases parametrizan el estilo, los campos
 *  del tooltip, los campos del popup y las capas de ortomosaico/conteo. */

let MAP_ID = 0;

/** Agrupa la respuesta del servidor con su capa Leaflet. `popup` es una
 *  instancia manual (no `layer.bindPopup`): así Leaflet nunca registra su
 *  propio listener de click en el layer, y el click del lote queda 100%
 *  bajo nuestro control (necesario para no competir con el control de
 *  medición — ver el click handler en `_initializeMap`). */
export type MapGroup<TResponse extends BaseResponse> = {
  lote: ClienteFincaLoteResponse;
  response: TResponse | null;
  layer: L.Polygon;
  popup: L.Popup | null;
};

export type PopupItem = {
  label: string;
  content: string;
};

export type PopupConfig<TResponse extends BaseResponse> = {
  bindItems(
    lote: ClienteFincaLoteResponse,
    response: TResponse | null,
  ): PopupItem[];
  withEditLote: boolean;
};

/** Clase base abstracta para renders de mapa. Las subclases concretas
 *  implementan {@link doInitData}, {@link doDispose}, {@link withOrtomosaico}
 *  y {@link withConteo}, y pueden sobrescribir {@link getStyle},
 *  {@link getTooltipFieldDefs} y {@link getPopupFieldDefs} para personalizar
 *  la apariencia y la información mostrada. */
export abstract class BaseMapRender<
  TResponse extends BaseResponse,
> implements IMapRender {
  /** Identificador único del mapa (auto-incremental). */
  private readonly _mapId: string;
  /** Finca seleccionada actualmente. */
  protected readonly _finca: ClienteFincaBaseResponse;

  constructor() {
    MAP_ID += 1;
    this._mapId = MAP_ID.toString();
    this._finca = serviceProvider.getFincaSelectedService().getRequired();
  }

  /** Instancia del mapa Leaflet (disponible tras {@link _initializeMap}). */
  private map: L.Map | null = null;

  /** Grupo de capas Leaflet que contiene los polígonos de los lotes. */
  private _featureGroup: FeatureGroup<ClienteFincaLoteResponse> =
    L.featureGroup<ClienteFincaLoteResponse>();

  /** Lote actualmente seleccionado (o null si no hay selección). */
  private _groupSelected: MapGroup<TResponse> | null = null;

  /** Lista de todos los lotes con sus capas. */
  protected _groupList: MapGroup<TResponse>[] = [];

  /** Callback opcional que se invoca al finalizar la inicialización del mapa. */
  private _handler: ((map: L.Map) => void) | null = null;

  /** Acciones del panel derecho (medición, estilos, etc.). */
  private _rigthActions = new RigthActions();

  /** Acciones del panel superior (tooltip, ortomosaico, conteo). */
  private _topActions = new TopActions<TResponse>();

  /** Registra un callback que se ejecuta al terminar {@link _initializeMap}.
   *  @param handler Recibe la instancia del mapa Leaflet ya creado. */
  setOnMapInitializeEnd(handler: (map: L.Map) => void): void {
    this._handler = handler;
  }

  /** Inicializa los datos específicos del mapa (lotes, aplicaciones, etc.).
   *  @returns Lista de respuestas con los datos a dibujar. */
  protected abstract doInitData(): Promise<TResponse[]>;

  protected abstract doGetResponse(
    lote: ClienteFincaLoteResponse,
    responseList: TResponse[],
  ): TResponse | null;

  /** Libera recursos específicos de la subclase. */
  protected abstract doDispose(): void;

  /** Indica si un lote puede seleccionarse (click). Por defecto, todos. Las
   *  subclases (p. ej. el picker de venta) pueden restringir; los no
   *  seleccionables se dibujan igual, pero no responden al click.
   *  @param _lote Lote a evaluar.
   *  @returns `true` si el lote es seleccionable. */
  protected isLoteSelectable(_lote: ClienteFincaLoteResponse): boolean {
    return true;
  }

  /** Estilo por defecto de cada lote no seleccionado. Las subclases pueden
   *  sobrescribir para cambiar colores, grosor, etc.
   *  @param _response Datos del lote.
   *  @returns Opciones de estilo de Leaflet. */
  protected getStyle(
    _lote: ClienteFincaLoteResponse,
    _response: TResponse | null,
  ): L.PathOptions {
    return {
      fillColor: "transparent",
      color: "rgb(224, 73, 13)",
      // weight: this._weightForZoom(),
      weight: 2.5,
      opacity: 1,
    };
  }

  protected abstract getConfigTopActions(): ConfigTopActions<TResponse>;

  protected getDefaultTooltipItems(): Tooltip<TResponse>[] {
    const result: Tooltip<TResponse>[] = [
      {
        label: "Nombre",
        get(_response, lote) {
          return lote.nombreLote;
        },
        key: "nombre",
      },
      {
        label: "Area Has",
        get(_response, lote) {
          return _2d(lote.areaHectareas);
        },
        key: "araHas",
      },
      {
        label: "Area Cds",
        get(_response, lote) {
          return _2d(lote.areaCuadras);
        },
        key: "araCds",
      },
    ];
    return result;
  }

  protected getPopupConfig(): PopupConfig<TResponse> | null {
    return null;
  }

  protected getDefaultPopupItems(lote: ClienteFincaLoteResponse): PopupItem[] {
    const result: PopupItem[] = [
      {
        label: "Nombre",
        content: lote.nombreLote,
      },
      {
        label: "Area Has",
        content: _2d(lote.areaHectareas),
      },
      {
        label: "Area Cds",
        content: _2d(lote.areaCuadras),
      },
    ];
    return result;
  }

  /** Notifica a la subclase que se seleccionó un lote.
   *  @param response Datos del lote seleccionado.
   *  @param bottomSheet Contenedor del bottom sheet del mapa. */
  protected onResponseSelected(
    _lote: ClienteFincaLoteResponse,
    _response: TResponse | null,
    _bottomSheet: HTMLDivElement,
  ): void {}

  /** Notifica a la subclase que se deseleccionó un lote.
   *  @param bottomSheet Contenedor del bottom sheet del mapa. */
  protected onLoteRemove(_bottomSheet: HTMLDivElement): void {}

  /** Renderiza el HTML del mapa vía Mustache. Devuelve mensaje de error si la
   *  finca no tiene ubicación o área definida. */
  render(): string {
    if (!this._finca.ubicacion || !this._finca.sateliteAreaGeografica) {
      return "La finca no tiene ubicacion y/o area definida, no deberia poder acceder aca";
    }
    return mustache.render(template, {
      mapId: this._mapId,
    });
  }

  /** Conecta el componente al DOM: busca los contenedores de controles, carga
   *  datos con {@link doInitData}, inicializa el mapa y monta los paneles
   *  de acciones (derecho y superior). */
  connect(): void {
    this._mapControlsTop = this._getElementById(
      `mapControlsTop-${this._mapId}`,
    );
    if (!this._mapControlsTop) return;

    this._mapInstance = this._getElementById(`mapInstance-${this._mapId}`);
    if (!this._mapInstance) return;

    this._mapControlsRight = this._getElementById(
      `mapControlsRight-${this._mapId}`,
    );
    if (!this._mapControlsRight) return;

    this._mapBottomSheet = this._getElementById(
      `mapBottomSheet-${this._mapId}`,
    );
    if (!this._mapBottomSheet) return;

    fx_createLoteGrpcService(this._finca.id)
      .getListAsync()
      .then((lotes) => {
        if (this._disposed) return;
        this.doInitData().then((responses) => {
          if (this._disposed) return;

          this._initializeMap(lotes, responses);

          if (!this.map) return;

          this._rigthActions.mount(this._mapControlsRight!, this.map, () => {
            if (!this._groupSelected) return;

            // Al activar medición, deselecciona por completo el lote activo
            // (no solo el resaltado): estilo, popup y bottom sheet.
            const style = this.getStyle(
              this._groupSelected.lote,
              this._groupSelected.response,
            );
            this._groupSelected.layer.setStyle(style);
            this._groupSelected.popup?.close();
            this.onLoteRemove(this._mapBottomSheet!);
            this._groupSelected = null;
          });

          this._topActions.mount(
            this._mapControlsTop!,
            this.map,
            this._groupList,
            this.getConfigTopActions(),
          );
        });
      });
  }

  /** Si el componente ya fue dispuesto. */
  protected _disposed: boolean = false;

  /** Libera los paneles de acciones, ejecuta {@link doDispose} de la subclase
   *  y marca como dispuesto. */
  dispose(): void {
    this._mapInstance?.removeEventListener("click", this._showEditLote);
    this._rigthActions.dispose();
    this._topActions.dispose();
    this.doDispose();
    this._disposed = true;
  }

  /** Contenedor del panel de controles superiores. */
  private _mapControlsTop: HTMLDivElement | null = null;
  /** Contenedor de la instancia del mapa. */
  private _mapInstance: HTMLDivElement | null = null;
  /** Contenedor del panel de controles derechos. */
  private _mapControlsRight: HTMLDivElement | null = null;
  /** Contenedor del bottom sheet del mapa. */
  private _mapBottomSheet: HTMLDivElement | null = null;

  /** Busca un elemento por ID en el DOM y lo devuelve como HTMLDivElement.
   *  @param divId ID del elemento.
   *  @returns El elemento o null si no existe. */
  private _getElementById = (divId: string): HTMLDivElement | null => {
    return document.getElementById(divId) as HTMLDivElement;
  };

  /** Inicializa el mapa Leaflet: crea la instancia, añade los tiles de Google,
   *  dibuja los polígonos de los lotes, configura popups y eventos de click,
   *  encuadra la vista y dispara el callback {@link _handler}.
   *  @param responseList Datos de los lotes a dibujar. */
  private _initializeMap(
    loteList: ClienteFincaLoteResponse[],
    responseList: TResponse[],
  ) {
    // Centro del MAPA
    const [longitud, latitud] = this._finca.ubicacion!.coordinates;
    const coordenadaLeaflet: L.LatLngExpression = [latitud, longitud];
    this.map = L.map(this._mapInstance!, {
      center: coordenadaLeaflet,
      zoom: 14,
      maxZoom: 20,
      zoomControl: false,
      attributionControl: false,
      preferCanvas: true,
    });

    // GOOGLE MAPS
    L.tileLayer(
      `https://mt{s}.google.com/vt?lyrs=y&x={x}&y={y}&z={z}&key=${GOOGLE_MAPS_API_KEY}`,
      { maxZoom: 20, subdomains: ["0", "1", "2", "3"] },
    ).addTo(this.map);

    // LAYERS DE CADA LOTE
    const this_ = this;
    const configPopup: PopupConfig<TResponse> | null = this.getPopupConfig();
    const withEditLote = configPopup !== null && configPopup.withEditLote;
    if (withEditLote) {
      this._mapInstance!.addEventListener("click", this._showEditLote);
    }
    this._featureGroup.clearLayers();
    loteList.forEach((lote) => {
      const geoJsonLayer = L.geoJSON(lote.areaGeograficaPolygon, {
        onEachFeature(_feature, layer: L.Polygon) {
          const response = this_.doGetResponse(lote, responseList);

          // Popup con los campos parametrizables: instancia manual (no
          // `layer.bindPopup`, ver el comentario de `MapGroup`).
          let popup: L.Popup | null = null;
          if (configPopup) {
            const popupFields = configPopup.bindItems(lote, response);

            const popupHtml = mustache.render(popupTemplate, {
              lote: lote.nombreLote,
              items: popupFields.map((item) => {
                return {
                  label: item.label,
                  content: item.content,
                };
              }),
              withEdit: withEditLote,
              loteId: lote.id,
            });
            popup = L.popup().setContent(popupHtml);
          }

          const group: MapGroup<TResponse> = {
            lote: lote,
            response: response,
            layer: layer,
            popup: popup,
          };
          this_._groupList.push(group);

          const style = this_.getStyle(lote, response);
          layer.setStyle(style);
        },
      });
      geoJsonLayer.addTo(this._featureGroup);
    });

    this._groupList.forEach((item) => {
      item.layer.on("click", (e: L.LeafletMouseEvent) => {
        // Con medición activa, el click del lote no selecciona: reenvía el
        // punto directo y frena el bubbling al mapa (si no, el click también
        // llegaría a `MeasureControl` vía `map.on("click", ...)` y el punto
        // se agregaría dos veces).
        if (this_._rigthActions.getMeasureControlActive()) {
          L.DomEvent.stopPropagation(e);
          this_._rigthActions.addMeasurePoint(e.latlng);
          return;
        }

        if (!this_.isLoteSelectable(item.lote)) return;
        const style = this_.getStyle(item.lote, item.response);
        if (this_._groupSelected) {
          // Estilo propio del lote que se desmarca (según sus propios datos),
          // no el del lote recién clickeado (`style`, calculado arriba).
          const prevStyle = this_.getStyle(
            this_._groupSelected.lote,
            this_._groupSelected.response,
          );
          this_._groupSelected.popup?.close();
          this_._groupSelected.layer.setStyle(prevStyle);
          this_.onLoteRemove(this_._mapBottomSheet!);
        }

        const layer = item.layer;
        if (item === this_._groupSelected) {
          this_.onLoteRemove(this_._mapBottomSheet!);
          this_._groupSelected = null;
          layer.setStyle(style);
          item.popup?.close();
          return;
        }

        style.color = "yellow";

        layer.setStyle(style);
        layer.bringToFront();
        item.popup?.setLatLng(layer.getCenter()).openOn(this_.map!);
        this_._groupSelected = item;
        this_.onResponseSelected(
          item.lote,
          item.response,
          this_._mapBottomSheet!,
        );
      });
    });
    this._featureGroup.addTo(this.map);

    // LIMITES Y MIN ZOOM
    this._fitToLotes();

    if (this._handler) this._handler(this.map);
  }

  /** Recalcula el tamaño del mapa tras un cambio de layout (p. ej. abrir/cerrar
   *  el bottom-sheet), **sin** alterar zoom/centro. Si en el futuro hiciera falta
   *  re-encuadrar a los lotes al togglear el sheet, usar `_fitToLotes()`. */
  protected invalidateMapSize(): void {
    this.map?.invalidateSize();
  }

  /** Re-encuadra el mapa a los lotes: recalcula `maxBounds` + `minZoom` y hace
   *  `fitBounds`. Se usa al inicializar y cuando cambia el tamaño disponible del
   *  mapa (p. ej. al abrir/cerrar el bottom-sheet). La vista arranca encuadrada a
   *  los lotes y el paneo se permite sobre lo que abarca la pantalla un nivel de
   *  zoom más atrás. */
  private _fitToLotes(): void {
    if (this._disposed) return;
    if (!this.map) return;
    // Antes de medir: `getSize`/`getBoundsZoom` leen el contenedor, y si el
    // layout cambió (bottom-sheet) Leaflet todavía tiene el tamaño viejo.
    this.map.invalidateSize();

    const bounds = this._featureGroup.getBounds();
    // `fx_acotarMapaA` es la implementación de referencia de este criterio
    // (viewport a `minZoom`, no `pad`) — vive aparte porque el paso 3 del
    // wizard de registro de finca la necesita igual.
    if (!fx_acotarMapaA(this.map, bounds)) return;

    this.map.fitBounds(bounds);
  }

  /** Listener delegado (enganchado una sola vez sobre el contenedor del mapa)
   *  del botón "Editar" del popup. Resuelve el lote por `data-lote-id` y abre su
   *  edición. Funciona aunque el popup/botón se cree después: el click burbujea
   *  desde el `popupPane` hasta `_mapInstance`. */
  /** Abre el modal de edición del lote y, al guardar, recarga la página actual
   *  (re-sincroniza los lotes). Reutilizable por subclases (p. ej. el bottom-sheet
   *  de ganadería, que está fuera del contenedor del mapa). */
  protected openLoteEdit(lote: ClienteFincaLoteResponse): void {
    new LoteEditModal(lote, this._finca, () =>
      serviceProvider.getLayoutService().reload(),
    ).init();
  }

  /** Listener delegado del botón "Editar" del popup: resuelve el lote por
   *  `data-lote-id` y abre su edición. */
  private _showEditLote = (e: MouseEvent): void => {
    const button = (e.target as HTMLElement | null)?.closest<HTMLElement>(
      "[data-lote-id]",
    );
    if (!button) return;

    const group = this._groupList.find(
      (g) => g.lote.id === button.dataset.loteId,
    );
    if (!group) return;

    this.openLoteEdit(group.lote);
  };
}
